import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import {
  comms,
  type CommsSession,
  type CommsSessionRequest,
  type DirectRoom,
  type DirectRoomDirectoryEntry,
} from '@/api/Comms';
import {
  CommsContext,
  type CommsContextValue,
  type CommsRuntimeStatus,
} from '../context/CommsContext';
import { getCommsClientInstanceId } from '../runtime/clientInstanceId';
import type {
  IncomingMatrixMessage,
  MatrixRuntimeConnectionState,
  MatrixRuntimeHandle,
  MatrixRuntimeLoader,
} from '../runtime/contracts';
import { loadMatrixRuntime } from '../runtime/loadMatrixRuntime';
import { ReadStateController } from '../matrix/readState';
import type { RoomTimelineStore } from '../matrix/timeline';
import {
  createProfileSessionCoordinator,
  type ProfileSessionCoordinator,
} from '../runtime/profileSessionCoordinator';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  data?: T;
}

interface CommsSessionApi {
  createSession(
    request: CommsSessionRequest,
  ): Promise<ApiResponse<CommsSession>>;
  endSession(request: CommsSessionRequest): Promise<ApiResponse<unknown>>;
  listDirectRooms(): Promise<ApiResponse<DirectRoomDirectoryEntry[]>>;
  ensureDirectRoom(operatorId: number): Promise<ApiResponse<DirectRoom>>;
}

interface CommsProviderProps extends PropsWithChildren {
  callerSubject?: string | null;
  api?: CommsSessionApi;
  loadRuntime?: MatrixRuntimeLoader;
  sessionCoordinator?: ProfileSessionCoordinator;
}

class CommsSessionManager {
  private snapshot = {
    status: 'idle' as CommsRuntimeStatus,
    matrixUserId: null as string | null,
    directRooms: new Map<number, string>() as ReadonlyMap<number, string>,
    unreadCounts: new Map<number, number>() as ReadonlyMap<number, number>,
    conversations: [] as CommsContextValue['conversations'],
  };
  private readonly listeners = new Set<() => void>();
  private readonly api: CommsSessionApi;
  private readonly loadRuntime: MatrixRuntimeLoader;
  private readonly sessionCoordinator: ProfileSessionCoordinator;
  private runtime: MatrixRuntimeHandle | null = null;
  private startPromise: Promise<void> | null = null;
  private stopPromise: Promise<void> | null = null;
  private hasServerSession = false;
  private lastError: Error | null = null;
  private generation = 0;
  private unsubscribeDirectRooms: (() => void) | null = null;
  private unsubscribeUnread: (() => void) | null = null;
  private unsubscribeIncomingMessages: (() => void) | null = null;
  private readonly incomingMessageListeners = new Set<
    (event: IncomingMatrixMessage) => void
  >();
  private directoryPromise: Promise<void> | null = null;
  private directoryRefreshQueued = false;
  private readState: ReadStateController | null = null;
  private unreadRevision = 0;
  private mountLease = 0;

  constructor(
    api: CommsSessionApi,
    runtimeLoader: MatrixRuntimeLoader,
    sessionCoordinator: ProfileSessionCoordinator,
  ) {
    this.api = api;
    this.loadRuntime = runtimeLoader;
    this.sessionCoordinator = sessionCoordinator;
    this.sessionCoordinator.subscribeRevoked(this.handleProfileSessionRevoked);
  }

  getSnapshot = () => this.snapshot;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  subscribeIncomingMessages = (
    listener: (event: IncomingMatrixMessage) => void,
  ) => {
    this.incomingMessageListeners.add(listener);
    return () => this.incomingMessageListeners.delete(listener);
  };

  retain = () => {
    const lease = ++this.mountLease;
    return () => {
      queueMicrotask(() => {
        if (lease !== this.mountLease) return;
        void this.stop({ endSession: false }).finally(() =>
          this.sessionCoordinator.dispose(),
        );
      });
    };
  };

  ensureStarted = () => {
    if (this.startPromise) return this.startPromise;
    if (
      this.snapshot.status === 'ready' ||
      this.snapshot.status === 'reconnecting'
    )
      return Promise.resolve();
    if (this.snapshot.status === 'failed')
      return Promise.reject(
        this.lastError ?? new Error('Messaging startup failed.'),
      );

    const generation = ++this.generation;
    this.startPromise = this.start(generation).finally(() => {
      this.startPromise = null;
    });
    return this.startPromise;
  };

  retryStart = async () => {
    if (this.snapshot.status !== 'failed') return this.ensureStarted();
    await this.stop({ endSession: true });
    return this.ensureStarted();
  };

  stop = (options?: { endSession?: boolean }) => {
    if (this.stopPromise) return this.stopPromise;
    this.stopPromise = this.stopInternal(options).finally(() => {
      this.stopPromise = null;
    });
    return this.stopPromise;
  };

  ensureDirectRoom = async (operatorId: number) => {
    const generation = this.generation;
    const response = await this.api.ensureDirectRoom(operatorId);
    const roomId = response.data?.roomId;
    if (!response.success || !roomId)
      throw new Error('Direct room could not be created.');
    if (generation !== this.generation) return roomId;
    const directRooms = new Map(this.snapshot.directRooms);
    directRooms.set(operatorId, roomId);
    await this.setDirectRooms(directRooms);
    return roomId;
  };

  getTimelineStore = (roomId: string): RoomTimelineStore | null =>
    this.runtime?.createTimelineStore(roomId) ?? null;

  selectRoom = (roomId: string | null) => this.readState?.selectRoom(roomId);

  markLatestVisible = (roomId: string, eventId: string) =>
    this.readState?.markLatestVisible(roomId, eventId);

  clearLatestVisible = (roomId: string, eventId: string) =>
    this.readState?.clearLatestVisible(roomId, eventId);

  sendText = (roomId: string, body: string) =>
    this.requireRuntime().sendText(roomId, body);

  retryMessage = (roomId: string, eventId: string) =>
    this.requireRuntime().retryMessage(roomId, eventId);

  sendImage = (
    roomId: string,
    file: File,
    onProgress?: Parameters<MatrixRuntimeHandle['sendImage']>[2],
  ) => this.requireRuntime().sendImage(roomId, file, onProgress);

  sendVoice = (
    roomId: string,
    voiceNote: Parameters<MatrixRuntimeHandle['sendVoice']>[1],
    onProgress?: Parameters<MatrixRuntimeHandle['sendVoice']>[2],
  ) => this.requireRuntime().sendVoice(roomId, voiceNote, onProgress);

  fetchMediaBlob = (mxc: string) => this.requireRuntime().fetchMediaBlob(mxc);

  watchRoomCall = (
    roomId: string,
    listener: Parameters<MatrixRuntimeHandle['watchRoomCall']>[1],
  ) => this.requireRuntime().watchRoomCall(roomId, listener);

  joinRtcCall = (roomId: string, liveKitServiceUrl: string) =>
    this.requireRuntime().joinRtcCall(roomId, liveKitServiceUrl);

  sendCallTerminal = (
    roomId: string,
    record: Parameters<MatrixRuntimeHandle['sendCallTerminal']>[1],
  ) => this.requireRuntime().sendCallTerminal(roomId, record);

  private start = async (generation: number) => {
    this.setStatus('starting');
    this.lastError = null;
    const clientInstanceId = getCommsClientInstanceId();
    try {
      const session = await this.sessionCoordinator.acquire(async () => {
        const response = await this.api.createSession({ clientInstanceId });
        if (!response.success || !response.data)
          throw new Error('Messaging session could not be created.');
        return response.data;
      });
      this.hasServerSession = true;
      this.ensureCurrentGeneration(generation);
      this.setMatrixUserId(session.matrixUserId);
      const runtimeModule = await this.loadRuntime();
      this.ensureCurrentGeneration(generation);
      const runtime = runtimeModule.createMatrixRuntime(
        session,
        this.handleRuntimeState,
      );
      this.runtime = runtime;
      this.unsubscribeIncomingMessages = runtime.subscribeIncomingMessages(
        this.publishIncomingMessage,
      );
      await runtime.start();
      this.ensureCurrentGeneration(generation);
      this.readState = new ReadStateController(async (roomId, eventId) => {
        await runtime.sendReadReceipt(roomId, eventId);
        if (this.runtime === runtime && generation === this.generation)
          this.refreshUnreadCounts();
      });
      this.unsubscribeDirectRooms = runtime.subscribeDirectRoomChanges(() => {
        void this.refreshDirectory().catch(() => undefined);
      });
      this.unsubscribeUnread = runtime.subscribeUnreadChanges(
        this.refreshUnreadCounts,
      );
      await this.refreshDirectory();
      this.ensureCurrentGeneration(generation);
      if (this.runtime === runtime) this.setStatus('ready');
    } catch (error) {
      this.clearRuntimeSubscriptions();
      this.runtime?.stop();
      this.runtime = null;
      const startupError =
        error instanceof Error ? error : new Error('Messaging startup failed.');
      if (generation === this.generation) {
        this.lastError = startupError;
        this.setStatus('failed');
      }
      throw startupError;
    }
  };

  private stopInternal = async (options?: { endSession?: boolean }) => {
    const starting = this.startPromise;
    this.generation += 1;
    this.directoryPromise = null;
    this.directoryRefreshQueued = false;
    this.unreadRevision += 1;
    this.setStatus('stopping');
    this.clearRuntimeSubscriptions();
    this.runtime?.stop();
    if (starting) {
      try {
        await starting;
      } catch {
        this.runtime?.stop();
      }
    }
    this.runtime = null;
    const shouldEndSession = options?.endSession ?? true;
    try {
      if (shouldEndSession && this.hasServerSession) {
        await this.sessionCoordinator.revoke(async () => {
          const response = await this.api.endSession({
            clientInstanceId: getCommsClientInstanceId(),
          });
          if (!response.success)
            throw new Error('Messaging session could not be ended.');
        });
      }
    } finally {
      this.hasServerSession = false;
      this.lastError = null;
      this.setMatrixUserId(null);
      void this.setDirectRooms(new Map());
      this.setStatus('idle');
    }
  };

  private handleRuntimeState = (state: MatrixRuntimeConnectionState) => {
    if (state === 'ready' && this.snapshot.status === 'starting') return;
    this.setStatus(state);
  };

  private handleProfileSessionRevoked = () => {
    this.hasServerSession = false;
    if (this.snapshot.status !== 'idle' && this.snapshot.status !== 'stopping')
      void this.stop({ endSession: false });
  };

  private refreshDirectory = () => {
    if (this.directoryPromise) {
      this.directoryRefreshQueued = true;
      return this.directoryPromise;
    }
    const generation = this.generation;
    const directoryPromise = this.loadDirectoryUntilCurrent(generation).finally(
      () => {
        if (this.directoryPromise !== directoryPromise) return;
        this.directoryPromise = null;
        this.directoryRefreshQueued = false;
      },
    );
    this.directoryPromise = directoryPromise;
    return directoryPromise;
  };

  private async loadDirectoryUntilCurrent(generation: number) {
    do {
      this.directoryRefreshQueued = false;
      await this.loadDirectory(generation);
    } while (this.directoryRefreshQueued && generation === this.generation);
  }

  private async loadDirectory(generation: number) {
    const response = await this.api.listDirectRooms();
    if (generation !== this.generation) return;
    if (!response.success || !response.data)
      throw new Error('Direct room directory could not be loaded.');
    const directRooms = new Map(
      response.data
        .slice()
        .sort((left, right) => left.operatorId - right.operatorId)
        .map((entry) => [entry.operatorId, entry.roomId] as const),
    );
    await this.setDirectRooms(directRooms);
  }

  private async setDirectRooms(directRooms: ReadonlyMap<number, string>) {
    const unreadCounts = new Map<number, number>();
    directRooms.forEach((_roomId, operatorId) =>
      unreadCounts.set(
        operatorId,
        this.snapshot.unreadCounts.get(operatorId) ?? 0,
      ),
    );
    this.snapshot = {
      ...this.snapshot,
      directRooms,
      unreadCounts,
      conversations: this.buildConversations(directRooms, unreadCounts),
    };
    this.emit();
    await this.reconcileUnreadCounts(directRooms);
  }

  private setMatrixUserId(matrixUserId: string | null) {
    if (this.snapshot.matrixUserId === matrixUserId) return;
    this.snapshot = { ...this.snapshot, matrixUserId };
    this.emit();
  }

  private refreshUnreadCounts = () => {
    void this.reconcileUnreadCounts(this.snapshot.directRooms);
  };

  private publishIncomingMessage = (event: IncomingMatrixMessage) => {
    this.incomingMessageListeners.forEach((listener) => listener(event));
  };

  private async reconcileUnreadCounts(
    directRooms: ReadonlyMap<number, string>,
  ) {
    const runtime = this.runtime;
    if (!runtime) return;
    const generation = this.generation;
    const revision = ++this.unreadRevision;
    const entries = await Promise.all(
      [...directRooms].map(async ([operatorId, roomId]) => {
        const unread = await runtime.reconcileUnreadCount(roomId);
        return [operatorId, roomId, Math.max(0, unread.count)] as const;
      }),
    );
    if (
      runtime !== this.runtime ||
      generation !== this.generation ||
      revision !== this.unreadRevision
    )
      return;
    const unreadCounts = new Map<number, number>();
    entries.forEach(([operatorId, roomId, count]) => {
      if (this.snapshot.directRooms.get(operatorId) === roomId)
        unreadCounts.set(operatorId, count);
    });
    this.snapshot = {
      ...this.snapshot,
      unreadCounts,
      conversations: this.buildConversations(
        this.snapshot.directRooms,
        unreadCounts,
      ),
    };
    this.emit();
  }

  private buildConversations(
    directRooms: ReadonlyMap<number, string>,
    unreadCounts: ReadonlyMap<number, number>,
  ) {
    return [...directRooms]
      .map(([operatorId, roomId]) => ({
        operatorId,
        roomId,
        ...this.runtime?.getRoomSummary(roomId),
        unreadCount: unreadCounts.get(operatorId) ?? 0,
      }))
      .sort(
        (left, right) =>
          (right.latestMessage?.timestamp ?? 0) -
            (left.latestMessage?.timestamp ?? 0) ||
          left.operatorId - right.operatorId,
      );
  }

  private clearRuntimeSubscriptions() {
    this.unsubscribeDirectRooms?.();
    this.unsubscribeDirectRooms = null;
    this.unsubscribeUnread?.();
    this.unsubscribeUnread = null;
    this.unsubscribeIncomingMessages?.();
    this.unsubscribeIncomingMessages = null;
    this.readState?.dispose();
    this.readState = null;
  }

  private ensureCurrentGeneration(generation: number) {
    if (generation !== this.generation)
      throw new Error('Messaging startup stopped.');
  }

  private requireRuntime() {
    if (!this.runtime) throw new Error('Messaging runtime is unavailable.');
    return this.runtime;
  }

  private setStatus(status: CommsRuntimeStatus) {
    if (this.snapshot.status === status) return;
    this.snapshot = { ...this.snapshot, status };
    this.emit();
  }

  private emit() {
    this.listeners.forEach((listener) => listener());
  }
}

export const CommsProvider = ({
  callerSubject = null,
  api = comms,
  loadRuntime = loadMatrixRuntime,
  sessionCoordinator,
  children,
}: CommsProviderProps) => {
  const sessionCoordinatorRef = useRef<ProfileSessionCoordinator | null>(null);
  sessionCoordinatorRef.current ??=
    sessionCoordinator ??
    createProfileSessionCoordinator(callerSubject ?? 'unauthenticated');
  const managerRef = useRef<CommsSessionManager | null>(null);
  managerRef.current ??= new CommsSessionManager(
    api,
    loadRuntime,
    sessionCoordinatorRef.current,
  );
  const manager = managerRef.current;
  useEffect(() => manager.retain(), [manager]);
  const snapshot = useSyncExternalStore(
    manager.subscribe,
    manager.getSnapshot,
    manager.getSnapshot,
  );
  const value = useMemo<CommsContextValue>(
    () => ({
      status: snapshot.status,
      matrixUserId: snapshot.matrixUserId,
      directRooms: snapshot.directRooms,
      unreadCounts: snapshot.unreadCounts,
      conversations: snapshot.conversations,
      subscribeIncomingMessages: manager.subscribeIncomingMessages,
      ensureStarted: manager.ensureStarted,
      retryStart: manager.retryStart,
      stop: manager.stop,
      ensureDirectRoom: manager.ensureDirectRoom,
      getTimelineStore: manager.getTimelineStore,
      selectRoom: manager.selectRoom,
      markLatestVisible: manager.markLatestVisible,
      clearLatestVisible: manager.clearLatestVisible,
      sendText: manager.sendText,
      retryMessage: manager.retryMessage,
      sendImage: manager.sendImage,
      sendVoice: manager.sendVoice,
      fetchMediaBlob: manager.fetchMediaBlob,
      watchRoomCall: manager.watchRoomCall,
      joinRtcCall: manager.joinRtcCall,
      sendCallTerminal: manager.sendCallTerminal,
    }),
    [manager, snapshot],
  );

  return (
    <CommsContext.Provider value={value}>{children}</CommsContext.Provider>
  );
};
